import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import L from 'leaflet'
import type { FeatureCollection } from 'geojson'
import {
  defaultPollingStationLayerStyle,
  highlightLayerStyle,
  selectedLayerStyle,
  unknown
} from '@/modules/map/utils/map-layer-styles'

import { trimAndLowercase } from '../../core/utils/string.util'
import { useConstituencyVoterStore } from '@/modules/voter/stores/constituency-voter.store'
import type PollingStation from '@/modules/data/models/polling-station.model'
import MapService from '@/modules/map/services/map-api.service'
import type { LegendItem } from '@/modules/map/models/legend-item.model'
import { ElectionType } from '@/modules/election/enums/election-type.enum'
import { useElectionStore } from '@/modules/election/stores/election-map.store'
import partyInfo from '@/assets/election/party_info.json'
import { VoterType } from '@/modules/voter/enums/voter-type.enum'

const mapService = new MapService()

export const useConstituencyMapStore = defineStore('constituencyMap', () => {
  const mVoterStore = useConstituencyVoterStore()
  const mElectionStore = useElectionStore()

  const isLoading = ref(false)

  const mConstituencyGeoJson = ref<FeatureCollection | null>(null)

  const mPollingStations = ref<PollingStation[]>([])
  const mSelectedPollingStations = ref<PollingStation[]>([])

  const mMap = ref<L.Map | null>(null)
  const mConstituencyBounds = ref<L.LatLngBounds | null | undefined>(null)
  const mDetailedColorMode = ref<boolean>(true)

  const isLegendVisible = ref<boolean>(true)

  const mSelectedVoterType = ref<string>(VoterType.PERCENTAGE)
  const mActiveTabIndex = ref(0)
  const isDemographics = ref<boolean>(false)

  function resetStore() {
    mSelectedPollingStations.value = []
    mDetailedColorMode.value = false
    // setActiveLayer()
  }

  const layers = {
    constituency: null as L.GeoJSON | null
  }

  watch(mActiveTabIndex, (newIndex) => {
    isDemographics.value = newIndex === 1
    updateLayers()
  })

  watch(mSelectedVoterType, () => {
    updateLayers()
  })

  watch(mSelectedPollingStations, () => {
    updateLayers()
  })

  watch(mDetailedColorMode, () => {
    updateLayers()
  })

  const mLegendItems = computed(() => {
    const legendItems: LegendItem[] = mDetailedColorMode.value
      ? [
          { id: 1, label: '', colorOne: '#37B2F7', colorTwo: '#85AC91', value: '2% and below' },
          { id: 2, label: '', colorOne: '#176FFC', colorTwo: '#4D8464', value: '3% - 59%' },
          { id: 3, label: '', colorOne: '#01009C', colorTwo: '#175D39', value: '60% and above' }
        ]
      : [
          { id: 2, label: '', colorOne: '#176FFC', colorTwo: '#4D8464', value: '59% and below' },
          { id: 3, label: '', colorOne: '#01009C', colorTwo: '#175D39', value: '60% and above' }
        ]

    return legendItems
  })

  const defaultMapView = {
    center: [6.6954967, -1.6194634],
    zoom: 10
  }

  function initializeMap(mapContainer: HTMLElement) {
    if (mapContainer) {
      mMap.value = L.map(mapContainer, {
        // center: defaultMapView.center as L.LatLngExpression,
        zoom: defaultMapView.zoom,
        zoomAnimation: true,
        zoomSnap: 0.25,
        zoomDelta: 0.25,
        wheelDebounceTime: 40
      })

      // Custom icon
      const customIcon = L.icon({
        iconUrl: '/images/pin.png', // Update with your icon path
        iconSize: [12, 12], // icon is 12x12 pixels
        iconAnchor: [6, 6], // center of the icon (half of width and height)
        popupAnchor: [0, -6] // popup opens above the icon center
      })
      for (const station of mPollingStations.value) {
        if (station.latitude && station.longitude) {
          const marker = L.marker([station.latitude, station.longitude], {
            icon: customIcon
          }).addTo(mMap.value as L.Map)
          // marker.bindTooltip('Welcome to Accra!').openTooltip();
          marker.bindPopup(station.name)
        }
      }

      // setActiveLayer()
    }
  }

  function initializeLayers() {
    if (!mMap.value) return

    layers.constituency = L.geoJSON(mConstituencyGeoJson.value, {
      style: defaultPollingStationLayerStyle,
      onEachFeature: onEachPollingStation
    })

    setActiveLayer()
  }

  function setActiveLayer() {
    if (!mMap.value) return

    layers.constituency?.addTo(mMap.value as L.Map)

    if (!mConstituencyBounds.value) {
      mConstituencyBounds.value = layers.constituency?.getBounds() ?? null
      if (mConstituencyBounds.value) {
        mMap.value.fitBounds(mConstituencyBounds.value)
      }
      console.log('mConstituencyBounds:', mConstituencyBounds.value)
    } else {
      mMap.value.fitBounds(mConstituencyBounds.value)
    }

    updateLayers()

    const bounds = layers.constituency?.getBounds()

    mMap.value.fitBounds(bounds!)
  }

  function updateLayers() {
    if (layers.constituency) {
      layers.constituency.eachLayer((layer: L.Layer) => {
        // const layerS = layer as L.Path
        const pollingStationCode = (layer as any).feature.properties.code
        const pollingStationName = (layer as any).feature.properties.name
        const pollingStationCodeNormalized = trimAndLowercase(pollingStationCode)
        const pollingStationNameNormalized = trimAndLowercase(pollingStationName)

        let fillColor: string | undefined = unknown
        let weight: number = defaultPollingStationLayerStyle.weight

        if (mSelectedPollingStations.value.length >= 1) {
          mSelectedPollingStations.value.forEach((s) => {
            if (trimAndLowercase(s.code) === pollingStationCodeNormalized) {
              fillColor = getFillColor(pollingStationNameNormalized, pollingStationCodeNormalized)
              weight = selectedLayerStyle.weight
            }
          })
        } else {
          fillColor = getFillColor(pollingStationNameNormalized, pollingStationCodeNormalized)
          weight = defaultPollingStationLayerStyle.weight
        }

        if ('setStyle' in layer) {
          const layerS = layer as L.Path
          const currentStyle = layerS.options
          layerS.setStyle({
            ...currentStyle,
            fillColor: fillColor,
            weight: weight
          })
        } else {
          console.error('Layer does not support setStyle')
        }
      })
    }
  }

  function getFillColor(area: string, code: string | null = null): string {
    if (!isDemographics.value) {
      // Get the appropriate election result based on the current level
      const result = mElectionStore.getResultPollingStation(ElectionType.PARLIAMENTARY, area, code!)
      if (!result || result.length < 1) {
        console.log('fill_color::else: ', code)
        return unknown // Return default color if no results are available
      }

      const [firstLeadingParty, secondLeadingParty] = result
      const firstRatio = firstLeadingParty.votesRatio ?? 0
      const secondRatio = secondLeadingParty.votesRatio ?? 0
      const thresholdLow = 0.02
      const thresholdMedium = 0.6

      // Helper to retrieve party colors based on the leading party's name
      const { color, colorMedium, colorLow } = getPartyColors(
        firstRatio >= secondRatio ? firstLeadingParty.party.name : secondLeadingParty.party.name
      )

      // Determine fill color based on vote ratios
      if (Math.abs(firstRatio - secondRatio) <= thresholdLow) {
        return firstRatio < secondRatio ? colorLow : colorLow // Return colorLow based on leading party
      }

      const leadingRatio = Math.max(firstRatio, secondRatio)
      return leadingRatio < thresholdMedium ? colorMedium : color
    } else {
      const growth = mVoterStore.getRegisteredVotersPollingStation(code!)

      if (!growth) {
        console.log('fill_color::else: ', area)
        return unknown // Return default color if no results are available
      }

      const percentage =
        mSelectedVoterType.value === VoterType.PERCENTAGE ? growth.percentage : growth.percentage

      if (percentage) {
        console.log('getFillColor::Percentage:', percentage)
        return getColor(percentage)
      } else {
        return unknown
      }
    }
  }

  function getColor(percentage: number): string {
    let colors = []
    if (mSelectedVoterType.value === VoterType.RATIO) colors = ratioThresholdConstituency
    else colors = percentageThreshold

    return colors.find((c) => percentage >= c.threshold)?.color ?? '#fecc5c'
  }

  const ratioThresholdConstituency = [
    { threshold: 1, color: '#800026' },
    { threshold: 0.99, color: '#BD0026' },
    { threshold: 0.8, color: '#FC4E2A' },
    { threshold: 0.6, color: '#FD8D3C' },
    { threshold: 0.4, color: '#FEB24C' },
    { threshold: 0.2, color: '#FED976' },
    { threshold: 0, color: '#fecc5c' }
  ]

  const percentageThreshold = [
    { threshold: 0.2, color: '#800026' },
    { threshold: 0.17, color: '#BD0026' },
    { threshold: 0.14, color: '#E31A1C' },
    { threshold: 0.11, color: '#FC4E2A' },
    { threshold: 0.09, color: '#FD8D3C' },
    { threshold: 0.06, color: '#FEB24C' },
    { threshold: 0.03, color: '#FED976' },
    { threshold: 0.01, color: '#fecc5c' }
  ]

  function getPartyColors(partyName: string) {
    const party = partyInfo.find((p) => trimAndLowercase(p.name) === trimAndLowercase(partyName))

    return {
      color: party?.color ?? unknown,
      colorMedium: party?.colorMedium ?? unknown,
      colorLow: mDetailedColorMode.value
        ? (party?.colorLow ?? unknown)
        : (party?.colorMedium ?? unknown)
    }
  }

  const fitBounds = () => {
    if (mMap.value && mConstituencyBounds.value) {
      console.log('fir_bounds')
      zoomToFeature(mConstituencyBounds.value)
    }
  }

  function zoomToFeature(bounds: L.LatLngBounds) {
    mMap.value?.flyToBounds(bounds, {
      duration: 0.5, // Duration of animation in seconds
      easeLinearity: 0.25 // Smooth out the animation
    })
  }

  function onEachPollingStation(feature: any, layer: L.Layer) {
    const pollingStationCode = feature.properties.code
    const pollingStationName = feature.properties.name

    const layerS = layer as L.Path

    if ('setStyle' in layer) {
      // const layerStyle = layer as L.Path;
      layerS.setStyle({
        ...defaultPollingStationLayerStyle
      })
      layerS
        .bindTooltip(
          `<h5>${pollingStationCode.toUpperCase()} - ${pollingStationName.toUpperCase()}</h5>`,
          { direction: 'top' }
        )
        .openTooltip()
    } else {
      console.error('Layer does not support setStyle')
    }
    // layerS.setStyle({
    //   ...defaultPollingStationLayerStyle,
    // })

    layer.on({
      click: (e) => {
        const pollingStationNameNormalized = trimAndLowercase(pollingStationName)

        const pollingStation = mPollingStations.value.find(
          (c) => trimAndLowercase(c.name) === pollingStationNameNormalized
        )

        if (pollingStation) {
          const constituencyAlreadySelected = mSelectedPollingStations.value.some(
            (c) => trimAndLowercase(c.name) === pollingStationNameNormalized
          )

          if (!constituencyAlreadySelected) {
            mSelectedPollingStations.value = [...mSelectedPollingStations.value, pollingStation]
          } else {
            mSelectedPollingStations.value = mSelectedPollingStations.value.filter(
              (c) => trimAndLowercase(c.name) !== pollingStationNameNormalized
            )
          }
        }
        // zoomToFeature(e.target.getBounds())
      },
      mouseover: (e: L.LeafletMouseEvent) => {
        const layer = e.target as L.Path
        if (!mSelectedPollingStations.value.includes(pollingStationCode)) {
          const currentStyle = layer.options
          const newStyle = {
            ...currentStyle,
            weight: 4,
            color: '#666',
            fillOpacity: highlightLayerStyle.fillOpacity
          }
          layer.setStyle(newStyle)
        }

        layer
          .bindTooltip(
            `<h5>${pollingStationCode.toUpperCase()} - ${pollingStationName.toUpperCase()}</h5>`,
            { direction: 'top' }
          )
          .openTooltip()
      },
      mouseout: (e: L.LeafletMouseEvent) => {
        const layer = e.target as L.Path
        if (!mSelectedPollingStations.value.includes(pollingStationCode)) {
          const currentStyle = layer.options
          const newStyle = {
            ...currentStyle,
            weight: defaultPollingStationLayerStyle.weight,
            color: defaultPollingStationLayerStyle.color,
            fillOpacity: defaultPollingStationLayerStyle.fillOpacity
          }
          layer.setStyle(newStyle)
        }
      }
    })
  }

  function toggleLegend() {
    isLegendVisible.value = !isLegendVisible.value
  }

  function showCentersOnMap() {
    const baseUrl = 'https://www.google.com/maps/dir/'
    const centersPaths = mPollingStations.value
      .map((coord) => `${coord.latitude},${coord.longitude}`)
      .join('/')

    const finalUrl = `${baseUrl}${centersPaths}`
    window.open(finalUrl, '_blank')
  }

  const getConstituencyShapeFile = async (constituency: string) => {
    const result = await mapService.getConstituencyShapeFile(constituency)

    result.fold(
      (value: any) => {
        mConstituencyGeoJson.value = value as FeatureCollection
        console.log('constituency shape file set')
        mPollingStations.value = extractPollingStations(mConstituencyGeoJson.value)
      },
      (error: any) => console.log(error)
    )
  }

  function extractPollingStations(geoJson?: FeatureCollection | null): PollingStation[] {
    if (geoJson) {
      const constituencies = geoJson.features
        .map((feature: any) => [
          feature.properties?.code.trim(),
          feature.properties?.name.trim(),
          feature.properties?.region.trim(),
          feature.properties?.constituency.trim(),
          feature.properties?.electoral_area.trim(),
          feature.properties?.latitude,
          feature.properties?.longitude
        ])
        .filter((constituency: any) => constituency !== undefined)

      return Array.from(new Set(constituencies))
        .sort((a, b) => a[0].localeCompare(b[0])) // Sort by constituency name
        .map(
          ([code, name, region, constituency, electoralArea, latitude, longitude], index: any) =>
            ({
              id: index + 1,
              code,
              name,
              region,
              constituency,
              electoralArea,
              latitude,
              longitude
            }) as PollingStation
        )
    }
    return []
  }

  function toggleColorMode() {
    mDetailedColorMode.value = !mDetailedColorMode.value
  }

  return {
    isLoading,
    isLegendVisible,
    pollingStations: mPollingStations,
    selectedPollingStations: mSelectedPollingStations,
    legendItems: mLegendItems,
    activeTabIndex: mActiveTabIndex,
    getConstituencyShapeFile,
    fitBounds,
    initializeMap,
    initializeLayers,
    // setActiveLayer,
    updateLayers,
    toggleLegend,
    toggleColorMode,
    showCentersOnMap,
    resetStore
  }
})
